/**
 * Catálogo de âncoras do tour guiado.
 *
 * Fonte única: os elementos da área do aluno carregam `data-tour="<key>"` e o
 * editor do admin oferece esta lista num dropdown, para que ninguém precise
 * digitar seletor CSS à mão. Para um alvo fora do catálogo o admin ainda pode
 * informar um seletor livre no passo (`targetSelector`).
 *
 * Ao adicionar uma âncora nova: registre a chave aqui E coloque o atributo
 * `data-tour` no elemento correspondente.
 */

export interface WalkthroughAnchor {
  /** Valor de `data-tour` no DOM. */
  key: string;
  /** Rótulo exibido no dropdown do admin. */
  label: string;
  /** Rota onde a âncora existe. `null` = presente em toda a área do aluno. */
  route: string | null;
  /** Agrupamento no dropdown. */
  group: string;
}

export const WALKTHROUGH_ANCHORS: WalkthroughAnchor[] = [
  // ----- Menu lateral -----
  {
    key: 'sidebar',
    label: 'Menu lateral (inteiro)',
    route: null,
    group: 'Menu lateral',
  },
  {
    key: 'sidebar-logo',
    label: 'Logo / início',
    route: null,
    group: 'Menu lateral',
  },
  {
    key: 'sidebar-nav',
    label: 'Lista de navegação',
    route: null,
    group: 'Menu lateral',
  },
  {
    key: 'sidebar-progress',
    label: 'Progresso / XP',
    route: null,
    group: 'Menu lateral',
  },
  {
    key: 'sidebar-devflix',
    label: 'Botão DevFlix',
    route: null,
    group: 'Menu lateral',
  },
  {
    key: 'sidebar-logout',
    label: 'Sair',
    route: null,
    group: 'Menu lateral',
  },

  // ----- Topo -----
  {
    key: 'header',
    label: 'Barra superior',
    route: null,
    group: 'Topo',
  },
  {
    key: 'header-admin',
    label: 'Atalho do painel admin',
    route: null,
    group: 'Topo',
  },
  {
    key: 'header-theme',
    label: 'Alternar tema',
    route: null,
    group: 'Topo',
  },
  {
    key: 'header-support',
    label: 'Suporte',
    route: null,
    group: 'Topo',
  },
  {
    key: 'header-notifications',
    label: 'Notificações',
    route: null,
    group: 'Topo',
  },
  {
    key: 'header-avatar',
    label: 'Menu do usuário',
    route: null,
    group: 'Topo',
  },

  // ----- Conteúdo -----
  {
    key: 'main-content',
    label: 'Área de conteúdo',
    route: null,
    group: 'Conteúdo',
  },
];

const ANCHORS_BY_KEY = new Map(WALKTHROUGH_ANCHORS.map((a) => [a.key, a]));

/** Agrupa o catálogo para renderizar o dropdown do admin. */
export function getAnchorGroups(): Array<{
  group: string;
  anchors: WalkthroughAnchor[];
}> {
  const groups = new Map<string, WalkthroughAnchor[]>();
  for (const anchor of WALKTHROUGH_ANCHORS) {
    const list = groups.get(anchor.group) ?? [];
    list.push(anchor);
    groups.set(anchor.group, list);
  }
  return Array.from(groups, ([group, anchors]) => ({ group, anchors }));
}

export function getAnchor(key: string): WalkthroughAnchor | undefined {
  return ANCHORS_BY_KEY.get(key);
}

/**
 * Seletor CSS efetivo de um passo. A chave do catálogo tem precedência; o
 * seletor livre é o fallback avançado.
 */
export function resolveSelector(step: {
  anchorKey?: string | null;
  targetSelector?: string | null;
}): string | null {
  if (step.anchorKey) return `[data-tour="${step.anchorKey}"]`;
  if (step.targetSelector) return step.targetSelector;
  return null;
}

/**
 * Chave de âncora para um item de menu vindo do banco. O menu do aluno é
 * dinâmico, então a âncora é derivada do título — nunca de posição.
 */
export function menuItemAnchorKey(title: string): string {
  const slug = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `menu-${slug}`;
}
